import { Injectable } from '@angular/core';
import { Payment } from '../models/payment';
import { PaymentHttpService } from './payment.http.service';
import { Observable } from 'rxjs/Observable';
import { LogService } from '../../shared/log/log.service';
import { FlashMessagesService } from '../../shared/flash-messages/service/flash.messages.service';
import { TranslateService } from '@ngx-translate/core';
import { List } from 'immutable';
import { ReplaySubject } from 'rxjs/ReplaySubject';
import { PaymentStatusUtils } from 'app/event/utils/payment-status-utils';

@Injectable()
export class PaymentStore {

  // This is the same as BehaviorSubject but without a default value
  private bufferSize = 1;
  private paymentList: ReplaySubject<List<Payment>> = new ReplaySubject<List<Payment>>(this.bufferSize);
  private latestPayment: ReplaySubject<Payment> = new ReplaySubject<Payment>(this.bufferSize);

  constructor(private paymentHttpService: PaymentHttpService,
              private logService: LogService,
              private flashMessagesService: FlashMessagesService,
              private translateService: TranslateService) {
  }

  addPayment(payment: Payment): Observable<any> {
    return this.paymentHttpService.add(payment)
      .catch(error => {
        this.flashMessagesService.error('MESSAGES.ERROR.ADD_PAYMENT', `Error adding Payment: ${error}`);
        return Observable.throw(error);
      })
      .map(result => {
        this.flashMessagesService.success('EVENT.COMMISSION.PAYMENT_SAVED');
        return result;
      });
  }

  get payments(): Observable<List<Payment>> {
    return this.paymentList.asObservable();
  }

  getLatestPayment(): Observable<Payment> {
    return this.latestPayment.asObservable();
  }

  loadPayments(eventId: number): Observable<List<Payment>> {
    return this.paymentHttpService.getPayments(eventId)
      .catch(error => {
        this.flashMessagesService.error(this.translateService.instant('MESSAGES.ERROR.GET_PAYMENTS'));
        this.logService.error(`Error adding Payment: ${error}`);
        return Observable.throw(error);
      })
      .map((result: List<Payment>) => {
        this.paymentList.next(result);
        return result;
      });
  }

  loadLatestPayment(eventId: number): Observable<Payment> {
    return this.paymentHttpService.getLatestPayment(eventId).catch( error => {
      this.flashMessagesService.error(this.translateService.instant('MESSAGES.ERROR.GET_LATEST_PAYMENT'));
      this.logService.error(`Error getting last Payment: ${error}`);
      return Observable.throw(error);
    }).map((result: Payment) => {
      this.latestPayment.next(result);
      return result;
    });
  }

  get commissionTotal(): Observable<number> {
    return this.payments.map(payments => {
      return payments
        .filter(payment => !this.isPaymentRejected(payment))
        .map(payment => payment.commissionAmount)
        .reduce((prev: number, current: number) => !!current ? prev + current : prev, 0);
    });
  }

  get containsRejectedPayments(): Observable<boolean> {
    return this.payments.map(payments => payments.some(this.isPaymentRejected));
  }

  /**
   * Gets the payment with the latest created date
   */
  get currentPayment(): Observable<Payment> {
    return this.payments.map((list: List<Payment>) => {
      return list.max((payment1: Payment, payment2: Payment) => {
        if (payment1.createdDate < payment2.createdDate) {
          return -1;
        } else if (payment1.createdDate > payment2.createdDate) {
          return 1;
        } else {
          return 0;
        }
      });
    });
  }

  /**
   * Returns true if the current payment exists and does not have a rejected or error status
   * @returns {Observable<R>}
   */
  get isCurrentPaymentValid(): Observable<boolean> {
    return this.currentPayment.map((lastPayment: Payment) => {
      return !!lastPayment && !this.isPaymentRejected(lastPayment);
    });
  }

  private isPaymentRejected(payment: Payment): boolean {
    return PaymentStatusUtils.isError(payment.status);
  }
}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/UAT-GroupPay/workspace/pioneer-web/src/app/event/service/payment.store.ts